import React, { useMemo } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { useChatContext } from 'stream-chat-react';
import { IconButton } from '@ds';
import './CustomChannelHeader.css';
import { StreamUserRole } from '@features/components/Chat/types';
import { HIDDEN_CHAT_ROLES, CHAT_LABEL_UNKNOWN_CHAT } from '@features/components/Chat/constants';
import { ChannelStateIndicators } from '@features/components/Chat/components/ChatContent/ChannelStateIndicators';
import { getChatMembersCount } from '@features/components/Chat/components/ChatContent/utils';
import { ChannelMenu } from './ChannelMenu';
import { StreamChannelType } from '@/api/apis/chat';

type ICustomChannelHeaderProps = {
  onBack?: () => void;
  showMenu?: boolean;
  HeaderContent?: React.ReactNode;
  setIsDetailsPanelOpen?: (isOpen: boolean) => void;
  togglePinChannel: () => void;
  toggleMuteChannel: () => void;
  isPinned: boolean;
  isMuted: boolean;
};

export const CustomChannelHeader = ({
  onBack,
  showMenu = true,
  HeaderContent,
  setIsDetailsPanelOpen = () => {},
  toggleMuteChannel,
  togglePinChannel,
  isMuted,
  isPinned,
}: ICustomChannelHeaderProps) => {
  const { client, channel } = useChatContext();

  const currentUserRole = client?.user?.role as StreamUserRole;
  const shouldHideOnline = HIDDEN_CHAT_ROLES.has(currentUserRole);

  const { onlineCount, totalCount } = useMemo(() => getChatMembersCount(channel), [channel]);

  const DefaultHeader = (
    <div className="flex items-center gap-2">
      {channel?.data?.image && (
        <img src={channel.data.image} alt="Chat Avatar" className="chat-avatar rounded-full w-8 h-8" />
      )}
      <div className="flex flex-col">
        <p className="text-m-m whitespace-normal flex items-center gap-1">
          {channel?.data?.name || CHAT_LABEL_UNKNOWN_CHAT}
          <ChannelStateIndicators
            isPinned={isPinned}
            isMuted={isMuted}
            channelType={channel?.type as StreamChannelType}
          />
        </p>
        <span className="text-s-r text-base-25">
          {totalCount} members, {onlineCount} online
        </span>
      </div>
    </div>
  );

  return (
    <div
      className="flex items-center justify-between p-2 text-base-5 space-x-2 custom-channel-header bg-base-80"
      data-hide-online={shouldHideOnline}
    >
      {onBack && (
        <IconButton
          size="xs"
          variant="light"
          startContent={<IoIosArrowBack />}
          onPress={onBack}
          className="flex items-center"
        />
      )}
      <div className="flex-1">{HeaderContent || DefaultHeader}</div>
      <div className="flex flex-col gap-1 items-center">
        {showMenu && (
          <ChannelMenu
            setIsDetailsPanelOpen={setIsDetailsPanelOpen}
            toggleMuteChannel={toggleMuteChannel}
            togglePinChannel={togglePinChannel}
            isMuted={isMuted}
            isPinned={isPinned}
          />
        )}
      </div>
    </div>
  );
};
